<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';

const store = useStore();
const router = useRouter();

const currentAccountId = computed(() => store.getters.getCurrentAccountId);

// Check if Langfuse integration is configured
const integrations = computed(
  () => store.getters['integrations/getAppIntegrations']
);
const langfuseIntegration = computed(() =>
  integrations.value?.find(integration => integration.id === 'langfuse')
);
const isLangfuseConfigured = computed(
  () => !!langfuseIntegration.value?.enabled
);

const langfuseUrl = computed(() => {
  if (!langfuseIntegration.value?.settings) return 'https://evals.vowchat.ai';

  const endpoint =
    langfuseIntegration.value.settings.endpoint_url ||
    'https://evals.vowchat.ai';
  // Remove /api/public/otel/v1/traces suffix if present
  return endpoint.replace(/\/api\/public\/otel\/v1\/traces$/, '');
});

const goToIntegrations = () => {
  router.push({
    name: 'settings_integrations',
    params: { accountId: currentAccountId.value },
  });
};

const openLangfuseDashboard = () => {
  window.open(langfuseUrl.value, '_blank');
};

const openLangfuseTraces = () => {
  // Open Langfuse with traces filtered by account_id
  const url = `${langfuseUrl.value}/traces?filter=account_id:${currentAccountId.value}`;
  window.open(url, '_blank');
};
</script>

<template>
  <div class="flex flex-col h-full">
    <div
      class="flex items-center justify-between p-6 border-b border-slate-75 dark:border-slate-800"
    >
      <div>
        <h1 class="text-2xl font-semibold text-slate-900 dark:text-slate-25">
          Evaluations
        </h1>
        <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">
          View and score AI Assistant traces in Langfuse
        </p>
      </div>
    </div>

    <div class="flex-1 overflow-auto p-6">
      <!-- Not Configured State -->
      <div v-if="!isLangfuseConfigured" class="max-w-2xl mx-auto mt-20">
        <EmptyState
          :title="$t('CAPTAIN.EVALS.NOT_CONFIGURED_TITLE')"
          :message="$t('CAPTAIN.EVALS.NOT_CONFIGURED_MESSAGE')"
        >
          <template #action>
            <woot-button color-scheme="primary" @click="goToIntegrations">
              {{ $t('CAPTAIN.EVALS.CONFIGURE_LANGFUSE') }}
            </woot-button>
          </template>
        </EmptyState>
      </div>

      <!-- Configured State -->
      <div v-else class="max-w-4xl mx-auto">
        <div
          class="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6"
        >
          <div class="flex items-start gap-4">
            <div class="flex-shrink-0">
              <div
                class="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/20 flex items-center justify-center"
              >
                <FluentIcon
                  icon="checkmark-circle"
                  size="24"
                  class="text-green-600 dark:text-green-400"
                />
              </div>
            </div>
            <div class="flex-1">
              <h3
                class="text-lg font-medium text-slate-900 dark:text-slate-25 mb-2"
              >
                {{ $t('CAPTAIN.EVALS.CONNECTED_TITLE') }}
              </h3>
              <p class="text-sm text-slate-600 dark:text-slate-300 mb-4">
                {{ $t('CAPTAIN.EVALS.CONNECTED_MESSAGE') }}
              </p>

              <div class="flex flex-wrap gap-3">
                <woot-button
                  color-scheme="primary"
                  icon="open"
                  @click="openLangfuseDashboard"
                >
                  {{ $t('CAPTAIN.EVALS.OPEN_DASHBOARD') }}
                </woot-button>

                <woot-button
                  variant="smooth"
                  color-scheme="secondary"
                  icon="filter"
                  @click="openLangfuseTraces"
                >
                  {{ $t('CAPTAIN.EVALS.VIEW_TRACES') }}
                </woot-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Info Cards -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <div
            class="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4"
          >
            <h4 class="font-medium text-slate-900 dark:text-slate-25 mb-2">
              {{ $t('CAPTAIN.EVALS.TRACES_TITLE') }}
            </h4>
            <p class="text-sm text-slate-600 dark:text-slate-300">
              {{ $t('CAPTAIN.EVALS.TRACES_DESCRIPTION') }}
            </p>
          </div>

          <div
            class="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4"
          >
            <h4 class="font-medium text-slate-900 dark:text-slate-25 mb-2">
              {{ $t('CAPTAIN.EVALS.SCORING_TITLE') }}
            </h4>
            <p class="text-sm text-slate-600 dark:text-slate-300">
              {{ $t('CAPTAIN.EVALS.SCORING_DESCRIPTION') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
